import React, { useEffect, useState } from 'react'
import { ApkInfo } from '@/types/apk.types'
import { apkService } from '@/services/apk.service'
import { Button } from '@/components/ui/Button'
import { toastError, toastSuccess } from '@/utils/toast'
import { downloadApkFile, formatApkActorName, getApkSourceLabel } from '@/utils/apk-utils'
import { Download } from 'phosphor-react'

export const AssignedApkSection: React.FC = () => {
  const [apkInfo, setApkInfo] = useState<ApkInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    let isMounted = true

    apkService
      .getAssigned()
      .then(data => {
        if (isMounted) {
          setApkInfo(data)
        }
      })
      .catch(error => {
        toastError(error instanceof Error ? error.message : "Erreur lors du chargement de l'APK")
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const file = await apkService.downloadAssigned()
      downloadApkFile(file)
      toastSuccess('APK téléchargée avec succès')
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Erreur lors du téléchargement de l'APK")
    } finally {
      setIsDownloading(false)
    }
  }

  const inheritedName = apkInfo ? formatApkActorName(apkInfo) : null

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-1">
        Application mobile (APK)
      </h3>
      <p className="text-sm text-[var(--color-text-secondary)] mb-4">
        Téléchargez la dernière version de l&apos;APK assignée à votre compte.
      </p>

      {isLoading ? (
        <p className="text-sm text-[var(--color-text-secondary)]">Chargement...</p>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-3 bg-[var(--color-gray-100)] rounded-lg">
          <div className="text-sm text-[var(--color-text-primary)] space-y-1">
            {apkInfo?.filename ? (
              <>
                <p>
                  Fichier : <span className="font-medium">{apkInfo.filename}</span>
                </p>
                <p className="text-[var(--color-text-secondary)]">
                  {getApkSourceLabel(apkInfo.source)}
                  {inheritedName ? ` — ${inheritedName}` : ''}
                </p>
              </>
            ) : (
              <p className="text-[var(--color-text-muted)]">Aucune APK disponible</p>
            )}
          </div>

          <Button
            onClick={handleDownload}
            isLoading={isDownloading}
            disabled={isDownloading || !apkInfo?.filename}
            variant="outline"
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" weight="regular" />
            Télécharger l&apos;APK
          </Button>
        </div>
      )}
    </div>
  )
}
